/**
 * Who gets into the panel, and who only into the API documentation.
 *
 * Two independent grants, both in the sign-in token: a panel role (client roles of the `kyc`
 * client: `admin`, `advanced`, `viewer`) opens the whole panel, the documentation included; a
 * documentation role (realm role `int-kyc` for partners and customer IT, `doc-kyc` for the
 * internal team) opens the Developers page and nothing else. The admin API enforces the same
 * rule; this side only decides what to show.
 */

import { useEffect, useSyncExternalStore } from 'react';
import { USER_ROLES } from '@/config/constants';
import { getCurrentRole } from '@/utils/auth';
import { apiMessage, developersApi } from './api';
import type { DeveloperInfo } from './types';

export const DOCUMENTATION_REALM_ROLES = ['int-kyc', 'doc-kyc'];

/** The panel role of a documentation-only user: no panel role, a documentation role. */
export const DEVELOPER_ROLE = 'Developer';

interface TokenRoles {
  realm_access?: { roles?: string[] };
}

export const hasDocumentationRole = (token: unknown): boolean =>
  ((token as TokenRoles | undefined)?.realm_access?.roles ?? []).some((role) =>
    DOCUMENTATION_REALM_ROLES.includes(role.toLowerCase()),
  );

// ── `developers/info`, fetched once per session and shared by the shell and the page ─────

export interface DeveloperInfoState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  info: DeveloperInfo | null;
  /** HTTP status of the failure, when there was one. */
  errorStatus?: number;
  /** The API's own message for the failure, ready to show. */
  errorMessage?: string;
}

let state: DeveloperInfoState = { status: 'idle', info: null };
const listeners = new Set<() => void>();

const setState = (next: DeveloperInfoState): void => {
  state = next;
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const loadDeveloperInfo = (): void => {
  if (state.status !== 'idle') return;
  setState({ status: 'loading', info: null });
  developersApi
    .info()
    .then((info) => setState({ status: 'ready', info }))
    .catch((error: { status?: number }) =>
      setState({ status: 'error', info: null, errorStatus: error?.status, errorMessage: apiMessage(error) }),
    );
};

export const useDeveloperInfo = (): DeveloperInfoState => {
  useEffect(loadDeveloperInfo, []);
  return useSyncExternalStore(subscribe, () => state);
};

// ── What the shell shows ────────────────────────────────────────────────────────────────

/** `panel`: everything. `documentation`: the Developers page alone. `none`: a notice. */
export type PanelAccess = 'panel' | 'documentation' | 'none';

/**
 * The token decides first; the API can only narrow it. A failed `info` call changes nothing,
 * so the panel does not depend on the documentation endpoint being up.
 */
export const usePanelAccess = (): PanelAccess => {
  const role = getCurrentRole();
  const { info } = useDeveloperInfo();
  if (role === USER_ROLES.NO_ACCESS) return 'none';
  if (role === DEVELOPER_ROLE || info?.isDocumentationOnly) return 'documentation';
  if (info && !info.canUsePanel) return 'none';
  return 'panel';
};
